import React, { useState, useEffect } from "react";
import {
  Box,
  Text,
  HStack,
  VStack,
  Image,
  Icon,
  Progress,
  chakra,
} from "@chakra-ui/react";
import { FaEllipsisH, FaTimes, FaChevronUp } from "react-icons/fa";
import type { AdPreviewCollaboration } from "../../utils/adPreviewCollaboration";
import CollaborationIdentityDisplay from "./CollaborationIdentityDisplay";

const Video = chakra("video");

// Helper function to determine if a URL is a video
const isVideoUrl = (url: string): boolean => {
  if (!url) return false;
  const videoExtensions = [".mp4", ".webm", ".ogg", ".mov", ".avi", ".mkv"];
  const lowerUrl = url.toLowerCase();
  return videoExtensions.some((ext) => lowerUrl.includes(ext));
};

export interface InstagramStoryPreviewProps {
  username: string;
  ctaText: string;
  mediaUrls: string[]; // Always an array - component determines single vs carousel
  mediaType: "single" | "carousel";
  profilePictureUrl?: string;
  collaboration?: AdPreviewCollaboration;
  fullWidth?: boolean;
}

const InstagramStoryPreview: React.FC<InstagramStoryPreviewProps> = ({
  username,
  ctaText,
  mediaUrls,
  mediaType,
  profilePictureUrl,
  collaboration,
  fullWidth = false,
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [progressValue, setProgressValue] = useState(0);

  // Use the mediaUrls array directly - component determines if it's carousel based on length
  const mediaItems = mediaUrls || [];
  const currentMedia = mediaItems[currentImageIndex];
  const isCarousel = mediaItems.length > 1;

  // Reset current image index when media changes
  useEffect(() => {
    setCurrentImageIndex(0);
    setProgressValue(0);
  }, [mediaItems]);

  // Auto-advance carousel stories
  useEffect(() => {
    if (!isCarousel || mediaItems.length <= 1) return;

    const interval = setInterval(() => {
      setProgressValue((prev) => {
        if (prev >= 100) {
          setCurrentImageIndex((current) =>
            current === mediaItems.length - 1 ? 0 : current + 1
          );
          return 0;
        }
        return prev + 2; // Increment by 2% every 100ms (5 seconds total per story)
      });
    }, 100);

    return () => clearInterval(interval);
  }, [isCarousel, mediaItems.length]);
  return (
    <Box
      bg="black"
      borderRadius="lg"
      overflow="hidden"
      maxW={fullWidth ? "100%" : ["100%", "70%"]}
      w="full"
      position="relative"
      aspectRatio="9/16"
      boxShadow="lg"
    >
      {/* Progress Bar(s) */}
      <Box position="absolute" top={1} left={0} right={0} zIndex={10} px={1}>
        {isCarousel ? (
          <HStack spacing={1} h="3px">
            {mediaItems.map((_, index) => (
              <Box
                key={index}
                flex={1}
                h="full"
                bg="rgba(255, 255, 255, 0.3)"
                borderRadius="full"
              >
                <Box
                  h="full"
                  bg="white"
                  borderRadius="full"
                  width={
                    index < currentImageIndex
                      ? "100%"
                      : index === currentImageIndex
                      ? `${progressValue}%`
                      : "0%"
                  }
                  transition="width 0.1s ease-out"
                />
              </Box>
            ))}
          </HStack>
        ) : (
          <Progress
            value={progressValue}
            size="xs"
            colorScheme="white"
            bg="transparent"
            sx={{
              "& .chakra-progress__filled-track": {
                bg: "white",
              },
            }}
          />
        )}
      </Box>

      {/* Top Header */}
      <HStack
        position="absolute"
        top={3}
        left={0}
        right={0}
        zIndex={10}
        px={3}
        justifyContent="space-between"
        alignItems="flex-start"
      >
        <HStack spacing={3} alignItems="center" flex={1} minW={0}>
          <CollaborationIdentityDisplay
            platform="instagram"
            tone="dark"
            primaryName={username}
            primaryProfilePictureUrl={profilePictureUrl}
            collaboration={collaboration}
            secondaryLabel={collaboration ? undefined : "Sponsored"}
            size="md"
          />
        </HStack>

        {/* Action Icons */}
        <HStack spacing={3}>
          <Icon as={FaEllipsisH} color="white" fontSize="sm" />
          <Icon as={FaTimes} color="white" fontSize="sm" />
        </HStack>
      </HStack>

      {/* Main Content Area */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg="gray.900"
      >
        {currentMedia ? (
          isVideoUrl(currentMedia) ? (
            <Video
              src={currentMedia}
              w="full"
              h="full"
              objectFit="cover"
              autoPlay
              muted
              loop
            />
          ) : (
            <Image
              src={currentMedia}
              alt="Instagram story content"
              w="full"
              h="full"
              objectFit="cover"
            />
          )
        ) : (
          <Box
            w="full"
            h="full"
            bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
            display="flex"
            alignItems="center"
            justifyContent="center"
            position="relative"
          >
            {/* Concert-like background pattern */}
            <Box
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              bg="linear-gradient(45deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)"
              opacity={0.8}
            />

            {/* Simulated confetti/particles */}
            <Box
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              backgroundImage="radial-gradient(circle at 20% 30%, rgba(255,255,255,0.1) 1px, transparent 1px), radial-gradient(circle at 80% 70%, rgba(255,255,255,0.1) 1px, transparent 1px), radial-gradient(circle at 40% 50%, rgba(255,255,255,0.1) 1px, transparent 1px)"
              backgroundSize="50px 50px, 30px 30px, 40px 40px"
              animation="float 3s ease-in-out infinite"
            />

            <Text color="white" fontSize="sm" fontWeight="semibold" zIndex={1}>
              {isCarousel ? "Story Carousel" : "Story Content"}
            </Text>
          </Box>
        )}
      </Box>

      {/* Top Gradient Overlay */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        height="120px"
        bg="linear-gradient(to bottom, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.3) 50%, transparent 100%)"
        zIndex={5}
        pointerEvents="none"
      />

      {/* Bottom Gradient Overlay */}
      <Box
        position="absolute"
        bottom={0}
        left={0}
        right={0}
        height="100px"
        bg="linear-gradient(to top, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.3) 50%, transparent 100%)"
        zIndex={5}
        pointerEvents="none"
      />

      {/* Call to Action Area */}
      <Box
        position="absolute"
        bottom={4}
        left={0}
        right={0}
        zIndex={10}
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
      >
        <VStack spacing={1}>
          <Icon as={FaChevronUp} color="white" fontSize="lg" />
          <Text fontSize="xs" color="white" fontWeight="medium">
            {ctaText}
          </Text>
        </VStack>
      </Box>
    </Box>
  );
};

export default InstagramStoryPreview;
